import { ReadonlyVec3, vec2, vec3 } from 'gl-matrix';
import { Grid } from '../../engine/data/common/Grid';
import { GenerationModelDetails } from '../dicom/dicomBase';
import { OptimizationObjective } from './optimizationObjective';
import {
    DVHVolumeUnits,
    DicomDose,
    DoseSummationType,
    DoseType,
    DoseUnit,
} from '../dicom/dicomDose';
import { DicomRoi } from '../dicom/dicomRoi';
import { CurveData, Dvh } from './dvh';
import { Plan } from './plan';
import { StructureSet } from './structureset';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { calculateDvh } from '~/engine/util/algorithms/sdf/DVHCalculation';
import { DoseTargetData } from '~/utils/transforms/doseTargetsTransforms';
import {
    DEFAULT_MAX_DOSE_RANGE,
    DEFAULT_MIN_DOSE_RANGE,
} from '~/engine/data/dose/DoseDistribution';
import { LTTB } from 'downsample';

/**
 * {@link DoseMatrix} is a container for dose information.
 *
 * Transient view properties (doseScale, normalizationValueGy, selectedRange,
 * targets) are stored directly on each instance so that per-dose settings
 * survive dose switching without leaking into global atoms.
 */
export class DoseMatrix {
    /**
     * Create a {@link DoseMatrix} from DICOM object
     * @param dose DICOM dose object
     * @returns DoseMatrix
     */
    static fromDose(dose: DicomDose): DoseMatrix {
        const res = vec3.fromValues(
            dose.pixelSpacing[0],
            dose.pixelSpacing[1],
            dose.sliceResolution
        );
        const size = vec3.fromValues(dose.cols, dose.rows, dose.frames);
        return new DoseMatrix(
            dose,
            Grid.fromDicom(
                size,
                res,
                dose.imagePosition,
                dose.imageOrientation
            ),
            dose.doseUnits,
            dose.doseType,
            dose.doseSummationType,
            dose.doseGridScaling,
            dose.referencedRtPlanUid
        );
    }

    public constructor(
        private readonly _dicomDose: DicomDose,
        grid: Grid,
        doseUnits: DoseUnit,
        doseType: DoseType,
        doseSummationType: DoseSummationType,
        doseGridScaling: number,
        private readonly _referencedRtPlanUid: string
    ) {
        this.grid = grid;
        this.doseUnits = doseUnits;
        this.doseType = doseType;
        this.doseSummationType = doseSummationType;
        this.doseGridScaling = doseGridScaling;
        // Initialize transient dose settings (not stored in DICOM)
        this._doseScale = 1.0;
    }

    public get sopInstanceUid(): string {
        return this._dicomDose.sopInstanceUid;
    }

    public get generationModel(): GenerationModelDetails | undefined {
        return this._dicomDose.generationModel;
    }

    public get isNew(): boolean {
        return this._dicomDose.isNew;
    }

    public get isExported(): boolean {
        return this._dicomDose.isExported;
    }

    public setExported(): void {
        this._dicomDose.setExported();
    }

    public get plan(): Plan | null {
        return this._plan;
    }

    public set plan(plan: Plan | null) {
        this._plan = plan;
    }

    public get structureSet(): StructureSet | null {
        if (this._plan) {
            return this._plan.structureSet;
        }
        return this._structureSet;
    }

    public set structureSet(structureSet: StructureSet | null) {
        if (this._plan && this._plan.structureSet !== structureSet) {
            throw new Error(
                'Cannot set structure set when plan has different structure set'
            );
        }
        if (!this._plan) {
            this._structureSet = structureSet;
        }
    }

    public get dicomDose(): DicomDose {
        return this._dicomDose;
    }

    public referencesPlanUid(uid: string): boolean {
        return uid === this._referencedRtPlanUid;
    }

    private _plan: Plan | null = null;

    // direct structure set reference, null if we have a plan reference instead
    private _structureSet: StructureSet | null = null;

    private _optimizationObjectives?: OptimizationObjective[];

    public get optimizationObjectives(): OptimizationObjective[] | undefined {
        return this._optimizationObjectives;
    }

    public set optimizationObjectives(
        objectives: OptimizationObjective[] | undefined
    ) {
        this._optimizationObjectives = objectives;
    }
    // ── Transient dose settings (not stored in DICOM) ──────────────────

    private _doseScale: number;
    private _normalizationValueGy?: number;
    private _selectedRange?: vec2;
    private _targets?: DoseTargetData[];

    public readonly grid: Grid;

    public readonly doseUnits: DoseUnit;

    public get dicomUid(): string {
        return this.sopInstanceUid;
    }

    public readonly doseType: DoseType;

    public readonly doseSummationType: DoseSummationType;

    private readonly doseGridScaling: number;

    public get pixelData(): Uint32Array {
        return this._dicomDose.pixelData;
    }

    private _realPixels?: Float32Array;

    public get realPixels() {
        if (!this._realPixels) {
            this._realPixels = new Float32Array(this.pixelData.values()).map(
                (v) => v * this.doseGridScaling
            );
        }
        return this._realPixels;
    }

    // Cached argmax of `realPixels`: both the max dose value (Gy) and the
    // patient-space (mm) coordinates of the winning voxel center, computed in
    // a single pass. `position` is null only for an empty dose grid.
    //
    // Note: `doseScale` is a uniform display-time multiplier and so does NOT
    // affect argmax — this cache is stable for the life of the instance. If a
    // future feature mutates `realPixels` (rather than scaling on display),
    // both fields must be invalidated together.
    private _doseMaxCache?: {
        valueGy: number;
        position: ReadonlyVec3 | null;
    };

    private getDoseMaxCache(): {
        valueGy: number;
        position: ReadonlyVec3 | null;
    } {
        if (this._doseMaxCache) {
            return this._doseMaxCache;
        }
        const data = this.realPixels;
        if (data.length === 0) {
            this._doseMaxCache = { valueGy: 0, position: null };
            return this._doseMaxCache;
        }
        let maxVal = -Infinity;
        let maxIdx = 0;
        for (let i = 0; i < data.length; i++) {
            if (data[i] > maxVal) {
                maxVal = data[i];
                maxIdx = i;
            }
        }
        this._doseMaxCache = {
            valueGy: maxVal,
            position: this.grid.indexToVoxelCenterWorldSpace(maxIdx),
        };
        return this._doseMaxCache;
    }

    public get doseMaxGy(): number {
        return this.getDoseMaxCache().valueGy;
    }

    /**
     * Patient-space (mm) coordinates of the voxel center holding the maximum
     * dose value. Returns null for an empty dose grid.
     *
     * On ties (multiple voxels with the same maximum value) the first voxel
     * encountered in raster order is returned, matching
     * {@link Image3D.findMaxIntensityPosition} semantics.
     */
    public get doseMaxPosition(): ReadonlyVec3 | null {
        return this.getDoseMaxCache().position;
    }

    /**
     * Dose scale factor (default 1.0). Transient property not stored in DICOM.
     */
    public get doseScale(): number {
        return this._doseScale;
    }

    public set doseScale(value: number) {
        this._doseScale = value;
    }

    /**
     * Normalization value in Gy (defaults to max dose). Transient property not stored in DICOM.
     */
    public get normalizationValueGy(): number {
        return this._normalizationValueGy ?? this.doseMaxGy;
    }

    public set normalizationValueGy(value: number) {
        this._normalizationValueGy = value;
    }

    /**
     * Colorwash dose range [min, max] in 0..1. Transient property not stored in DICOM.
     * Defaults to the application-wide default range.
     */
    public get selectedRange(): vec2 {
        return (
            this._selectedRange ??
            vec2.fromValues(DEFAULT_MIN_DOSE_RANGE, DEFAULT_MAX_DOSE_RANGE)
        );
    }

    public set selectedRange(value: vec2) {
        this._selectedRange = value;
    }

    /**
     * Prediction targets associated with this dose. Transient property not stored in DICOM.
     */
    public get targets(): DoseTargetData[] | undefined {
        return this._targets;
    }

    public set targets(value: DoseTargetData[] | undefined) {
        this._targets = value;
    }

    /**
     * Cached calculated DVHs keyed by ROI reference.
     * Each cache entry stores the DVH plus the ROI SDF/contour version used to compute it.
     */
    private _calculatedDvhs: Map<
        RegionOfInterest,
        { version: number; dvh: Dvh }
    > = new Map();

    /**
     * Resolve the version number of the ROI's SDF/contours.
     * Supports new sdfVersion or legacy contourVersion (fallback).
     */
    private _getRoiVersion(roi: RegionOfInterest): number {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const anyRoi = roi as any;
        return (
            (typeof anyRoi.sdfVersion === 'number' && anyRoi.sdfVersion) ||
            (typeof anyRoi.contourVersion === 'number' &&
                anyRoi.contourVersion) ||
            0
        );
    }
    private _cachedDicomCurves?: {
        scale: number;
        resolution: number;
        curves: CurveData[];
    };

    public buildDicomDvhCurves(resolution: number = 100): CurveData[] {
        if (
            this._cachedDicomCurves &&
            this._cachedDicomCurves.scale === this._doseScale &&
            this._cachedDicomCurves.resolution === resolution
        ) {
            return this._cachedDicomCurves.curves;
        }

        const curves: CurveData[] = [];
        const scale = this._doseScale;

        for (const [roi, dvh] of this.getDicomDvhs() ?? []) {
            const fullPoints = Array.from(dvh.dvhData, (y, x) => ({
                x: x * dvh.binWidth * scale,
                y,
            }));

            curves.push({
                // Pre-downsampled — chart can use data directly
                data: LTTB(fullPoints, resolution) as {
                    x: number;
                    y: number;
                }[],
                color: `rgb(${roi.color.R}, ${roi.color.G}, ${roi.color.B})`,
                name: roi.name,
                visible: roi.visible,
                doseMaxGy: dvh.doseMaxGy * scale,
                doseMinGy: dvh.doseMinGy * scale,
                doseMeanGy: dvh.doseMeanGy * scale,
                roiNumber: roi.roiNumber,
            });
        }

        this._cachedDicomCurves = { scale, resolution, curves };
        return curves;
    }

    /**
     * Get (and cache) a calculated DVH for the ROI.
     * Recomputes automatically if ROI version has changed since last calculation.
     */
    public async getCalculatedDvh(roi: RegionOfInterest): Promise<Dvh> {
        const currentVersion = this._getRoiVersion(roi);
        const cached = this._calculatedDvhs.get(roi);

        if (cached && cached.version === currentVersion) {
            return cached.dvh;
        }

        const dvhRes = await calculateDvh(this, roi);
        const dvh = new Dvh(
            dvhRes.cumDvhRelative,
            dvhRes.binWidthGy,
            dvhRes.doseMaxGy,
            dvhRes.doseMinGy,
            dvhRes.doseMeanGy,
            dvhRes.structureVolume_mm3 / 1000
        );

        this._calculatedDvhs.set(roi, {
            version: currentVersion,
            dvh,
        });

        return dvh;
    }

    /**
     * Invalidate a single ROI DVH cache entry (e.g., after external forced ROI update).
     */
    public clearCalculatedDvh(roi: RegionOfInterest): void {
        this._calculatedDvhs.delete(roi);
    }

    /**
     * Clear all cached calculated DVHs.
     */
    public clearAllCalculatedDvhs(): void {
        this._calculatedDvhs.clear();
    }

    private _dicomDvhs: Map<DicomRoi, Dvh> = new Map();

    public getDicomDvh(roi: DicomRoi): Dvh | undefined {
        if (!this._dicomDvhs.has(roi)) {
            this._loadDicomDvhs();
        }
        return this._dicomDvhs.get(roi);
    }

    public setDicomDvh(roi: DicomRoi, dvh: Dvh) {
        this._dicomDvhs.set(roi, dvh);
    }

    public getDicomDvhs(): Map<DicomRoi, Dvh> {
        this._loadDicomDvhs();
        return this._dicomDvhs;
    }

    private _loadDicomDvhs() {
        if (this.structureSet) {
            this.structureSet.rois.forEach((roi) => {
                const dicomDvh = this._dicomDose.dvhSequence.find(
                    (d) => d.referencedRoiNumber === roi.roiNumber
                );
                if (dicomDvh) {
                    const dvhLength = Math.ceil(dicomDvh.dvhData.length / 2);
                    const dvhPoints = new Float64Array(dvhLength);
                    const binWidth = dicomDvh.dvhData[0];
                    const toPercentageFactor = 100 / dicomDvh.dvhData[1];
                    for (let i = 0; i < dicomDvh.dvhData.length; i += 2) {
                        if (dicomDvh.dvhData[i] !== binWidth) {
                            console.warn(
                                `DVH bin width mismatch, skipping ROI ${roi.name}`
                            );
                            return;
                        }
                        if (dicomDvh.dvhVolumeUnits == DVHVolumeUnits.PERCENT) {
                            dvhPoints[i / 2] = dicomDvh.dvhData[i + 1];
                        } else if (
                            dicomDvh.dvhVolumeUnits == DVHVolumeUnits.CM3
                        ) {
                            dvhPoints[i / 2] =
                                dicomDvh.dvhData[i + 1] * toPercentageFactor;
                        }
                    }
                    this._dicomDvhs.set(
                        roi,
                        new Dvh(
                            dvhPoints,
                            binWidth,
                            dicomDvh.dvhMaximumDose,
                            dicomDvh.dvhMinimumDose,
                            dicomDvh.dvhMeanDose
                        )
                    );
                }
            });
        }
    }
}
